import { cn } from '@shadcn/utils';
import { useEffect, useState } from 'react';
import { commercialMenu, residentialMenu } from './menus';
import { SidebarHeader } from './SidebarHeader';
import { VerticalNavbar } from './VerticalNavbar';
import { useAppLayout } from 'app/utils/AppLayout';
import useFeatureAccess from 'app/services/feature-access';

const MOBILE_BREAKPOINT = '(max-width: 640px)';

export const Sidebar = () => {
  const { currentPlan } = useFeatureAccess();
  const { sidebarOpen, setSidebarOpen } = useAppLayout();
  const [isMobile, setIsMobile] = useState(false);
  const menu = currentPlan === 'residential' ? residentialMenu : commercialMenu;

  useEffect(() => {
    // Safety check for SSR and old browsers
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT);

    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
      if (e.matches) {
        setSidebarOpen(false);
      }
    };

    // Initial check
    handleResize(mobileMediaQuery);

    // Try modern event listener first
    try {
      mobileMediaQuery.addEventListener('change', handleResize);
      return () => mobileMediaQuery.removeEventListener('change', handleResize);
    } catch (e) {
      // Fallback for older browsers
      try {
        // @ts-ignore - for older browsers that use addListener
        mobileMediaQuery.addListener(handleResize);
        return () => {
          // @ts-ignore - for older browsers that use removeListener
          mobileMediaQuery.removeListener(handleResize);
        };
      } catch (e) {
        console.warn('Media queries not fully supported in this browser');
      }
    }
  }, [setSidebarOpen]);

  // Default to mobile view if matchMedia isn't supported
  const shouldUseMobileView =
    typeof window === 'undefined' || !window.matchMedia ? window?.innerWidth <= 640 : isMobile;

  return (
    <div
      className={cn(
        'sticky top-0 flex h-screen flex-col border-r border-r-border/50 bg-background',
        'transition-all duration-300 ease-in-out',
        {
          'w-[260px] pt-4': sidebarOpen && !shouldUseMobileView,
          'w-[60px] pt-2': !sidebarOpen || shouldUseMobileView,
          'fixed z-50 shadow-lg': shouldUseMobileView,
        }
      )}
    >
      <div
        className={cn('flex min-h-0 flex-1 flex-col', {
          'overflow-hidden': shouldUseMobileView,
        })}
      >
        <SidebarHeader isMobile={shouldUseMobileView} />
        <VerticalNavbar items={menu} isMobile={shouldUseMobileView} />
      </div>
    </div>
  );
};
